#!/usr/bin/env node
/**
 * Paean CLI
 * Command-line interface for Paean AI task management and agent interaction
 */

import { Command } from 'commander';
import { loginCommand } from './commands/login.js';
import { logoutCommand } from './commands/logout.js';
import { tasksCommand } from './commands/tasks.js';
import { contextCommand } from './commands/context.js';
import { serveCommand } from './commands/serve.js';
import { validateCommand } from './commands/validate.js';
import { agentCommand, runAgentMode } from './commands/agent.js';
import { traceCommand } from './commands/trace.js';
import { gatewayCommand } from './commands/gateway.js';
import { promptCommand } from './commands/prompt.js';
import { historyCommand, resumeCommand, showCommand, starCommand, renameCommand, deleteCommand, archiveCommand } from './commands/history.js';
import { devicesCommand } from './commands/devices.js';
import { configCommand } from './commands/config.js';
import { mcpCommand } from './commands/mcp.js';
import { creditsCommand } from './commands/credits.js';
import { usageCommand } from './commands/usage.js';
import { sparkletCommand } from './commands/sparklet.js';
import { completionCommand } from './commands/completion.js';
import { wechatCommand } from './commands/wechat.js';
import { updateCommand } from './commands/update.js';
import { a8eCommand } from './commands/a8e.js';
import { workersCommand } from './commands/workers.js';
import { getConfigPath } from './utils/config.js';
import { BUILD_MODE } from './build-info.js';
import { CLI_VERSION } from './version.js';

const program = new Command();

program
  .name('paean')
  .description('Paean AI CLI — interactive agent, task management, MCP integration, and autonomous worker')
  .version(CLI_VERSION, '-v, --version')
  .option('--config', 'Show config file path')
  .option('--no-mcp', 'Disable local MCP server integration')
  .option('--no-color', 'Disable colored output')
  .option('-d, --debug', 'Enable debug logging')
  .option('-m, --message <message>', 'Send a single message to agent')
  .option('-r, --resume <conversationId>', 'Resume a previous conversation session')
  .option('-g, --gateway', 'Enable gateway relay for remote clients')
  .option('--gateway-interval <ms>', 'Gateway poll interval in milliseconds', '3000')
  .option('-n, --session-name <name>', 'Session name visible to web clients (used with --gateway)')
  .option('--wechat', 'Enable WeChat channel gateway')
  .option('-t, --tier <tier>', 'Model tier: lite, flash, pro (default: flash)', 'flash');

if (BUILD_MODE === 'development') {
  program.option('--dev', 'Use dev environment (dev.api.paean.ai)');
}

program
  .hook('preAction', async (thisCommand) => {
    const opts = thisCommand.opts();
    if (opts.dev && BUILD_MODE === 'development') {
      process.env.PAEAN_API_URL = 'https://dev.api.paean.ai';
      process.env.PAEAN_WEB_URL = 'https://dev.app.paean.ai';
      process.env.PAEAN_CONFIG_DIR = 'paean-dev';
      if (opts.debug) {
        console.error('[dev] API → https://dev.api.paean.ai');
      }
    }
  })
  .action(async (options) => {
    if (options.config) {
      console.log(getConfigPath());
      return;
    }

    // Validate message if provided
    if (options.message) {
      const message = String(options.message).trim();
      if (!message) {
        console.error('❌ Error: Message cannot be empty.');
        process.exit(1);
      }
      if (message.length > 10000) {
        console.error('❌ Error: Message is too long (max 10000 characters).');
        process.exit(1);
      }
      options.message = message;
    }

    // Default action: start agent mode
    const tier = (['lite', 'flash', 'pro'].includes(options.tier) ? options.tier : 'flash') as 'lite' | 'flash' | 'pro';
    await runAgentMode({
      mcp: options.mcp !== false,
      debug: options.debug,
      message: options.message,
      resumeConversationId: options.resume,
      gatewayEnabled: options.gateway ?? false,
      gatewayPollInterval: parseInt(options.gatewayInterval, 10) || 3000,
      gatewaySessionName: options.sessionName,
      wechatEnabled: options.wechat ?? false,
      modelTier: tier,
    });
  });

// Register commands
program.addCommand(agentCommand);
program.addCommand(promptCommand);
program.addCommand(loginCommand);
program.addCommand(logoutCommand);
program.addCommand(tasksCommand);
program.addCommand(contextCommand);
program.addCommand(serveCommand);
program.addCommand(validateCommand);
program.addCommand(traceCommand);
program.addCommand(gatewayCommand);
program.addCommand(historyCommand);
program.addCommand(resumeCommand);
program.addCommand(showCommand);
program.addCommand(starCommand);
program.addCommand(renameCommand);
program.addCommand(deleteCommand);
program.addCommand(archiveCommand);
program.addCommand(devicesCommand);
program.addCommand(configCommand);
program.addCommand(mcpCommand);
program.addCommand(creditsCommand);
program.addCommand(usageCommand);
program.addCommand(sparkletCommand);
program.addCommand(completionCommand);
program.addCommand(wechatCommand);
program.addCommand(updateCommand);
program.addCommand(a8eCommand);
program.addCommand(workersCommand);
// Parse arguments
program.parse();
